import { Button, Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { CryptoSettingsIcon, InfoIcon } from 'BreetAssets';
import { Checkbox, getReceiveCurrencyLabel, Loader } from 'BreetComponents';
import { capitalizeFirstLetter, cryptoSettingsOptions, viewHelpArticle } from 'BreetHelpers';
import { useCryptoSettings } from 'BreetHooks';

const CryptoSettings = ({ isModal }: { isModal?: boolean }) => {
	const {
		handleCurrencyChange,
		handleSaveChanges,
		hasUnsavedChanges,
		isUpdating,
		isLoading,
		selectedReceiveCurrency,
		saveChangesRef,
		fiatMeta,
		currentReceiveCurrency,
	} = useCryptoSettings(isModal);

	if (isLoading) {
		return <Loader />;
	}

	return (
		<div className='cryptosettings subbodycontent_main bg-primary-500 border-dark-navy'>
			<Typography
				className='color-neutral-50'
				variant='sub_head'
			>
				Which wallet should your crypto go?
			</Typography>
			<div className='cryptosettings_list'>
				{cryptoSettingsOptions
					.filter((option) => {
						const fiatCurrencies = ['ngn', 'ghs'] as const;
						const isFiatOption = fiatCurrencies.includes(option.currency as (typeof fiatCurrencies)[number]);
						return !isFiatOption || option.currency === fiatMeta.currencyLower;
					})
					.map((option) => {
						const isActive = option.currency === selectedReceiveCurrency;

						return (
							<div
								key={option.currency}
								className={classNameMerger('cryptosettings_list_item border-dark-navy', isActive && 'active bg-primary-400')}
							>
								<label
									htmlFor={option.currency}
									className='cryptosettings_list_item_label'
									aria-label={`${capitalizeFirstLetter(getReceiveCurrencyLabel(option.currency))} Wallet`}
								/>
								<div className='cryptosettings_list_item_head'>
									<CryptoSettingsIcon variant={option.currency} />
									<Checkbox
										id={option.currency}
										type='radio'
										name='receiveCurrency'
										checked={isActive}
										value={option.currency}
										onChange={handleCurrencyChange}
									/>
								</div>
								<div className='cryptosettings_list_item_content'>
									<div className='cryptosettings_list_item_content_title'>
										<Typography
											className='title'
											variant='paragraph_1'
										>
											{capitalizeFirstLetter(getReceiveCurrencyLabel(option.currency))} Wallet
										</Typography>
										{currentReceiveCurrency === option.currency && (
											<span className='activebadge'>
												<Typography
													as='span'
													variant='paragraph_2'
												>
													Active
												</Typography>
											</span>
										)}
									</div>
									<Typography
										variant='paragraph_2'
										className={classNameMerger('description', isActive && 'color-primary-100')}
									>
										{option.description}
									</Typography>
								</div>
							</div>
						);
					})}
			</div>
			<div className='cryptosettings_cta'>
				<Button
					variant='outline'
					size='sm'
					className='cryptosettings_cta_howitworks'
					onClick={viewHelpArticle('13925878')}
				>
					<InfoIcon className='apphome_infoicon svg-light' />
					<Typography variant='paragraph_2'>How it works</Typography>
				</Button>
				<Button
					size='lg'
					ref={saveChangesRef}
					className='cryptosettings_cta_update'
					disabled={!hasUnsavedChanges || isUpdating}
					isLoading={isUpdating}
					onClick={handleSaveChanges}
				>
					Save Changes
				</Button>
			</div>
		</div>
	);
};

export default CryptoSettings;
